import React from 'react'
import { homeApi } from '../../services/api'
import type { OrgAuditReadinessInputs } from './auditReadinessModel'

const defaultInputs: OrgAuditReadinessInputs = {
  unifiedStandards: [],
  orgSecurityPercent: null,
  ready: false,
}

const AuditReadinessContext = React.createContext<OrgAuditReadinessInputs>(defaultInputs)

export function useOrgAuditReadinessInputs(): OrgAuditReadinessInputs {
  return React.useContext(AuditReadinessContext)
}

export function AuditReadinessProvider({
  children,
  token,
}: {
  children: React.ReactNode
  token: string | null | undefined
}) {
  const [inputs, setInputs] = React.useState<OrgAuditReadinessInputs>(defaultInputs)

  React.useEffect(() => {
    if (!token) {
      setInputs(defaultInputs)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const [unified, health] = await Promise.all([
          homeApi.getUnifiedAssessmentSnapshots(),
          homeApi.getSecurityHealth().catch(() => null),
        ])
        if (cancelled) return
        setInputs({
          unifiedStandards: (unified?.standards || []) as OrgAuditReadinessInputs['unifiedStandards'],
          orgSecurityPercent:
            health?.percent != null && Number.isFinite(Number(health.percent))
              ? Number(health.percent)
              : null,
          ready: true,
        })
      } catch {
        if (!cancelled) setInputs((prev) => ({ ...prev, ready: true }))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  return <AuditReadinessContext.Provider value={inputs}>{children}</AuditReadinessContext.Provider>
}
